import {
  createContext,
  use,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { assertNever } from '@/lib/assert-never'
import type { TestResult } from '@/lib/test-harness/types'
import type {
  OperationTestResult,
  TestFailure,
  UserFrame,
} from '@/lib/types/test-cases'
import { useProblemOptional } from './problem-context'

// Re-export for backwards compatibility and so consumers get the full
// result contract from one module
export type { OperationTestResult, TestFailure, TestResult, UserFrame }

// =============================================================================
// Types for Code Execution Results
// =============================================================================

/**
 * Summary of test execution
 */
export interface TestSummary {
  total: number
  passed: number
  failed: number
}

/**
 * Result from /api/execute/run (synchronous execution)
 */
export interface RunExecutionResult {
  executionId: string
  success: boolean
  results: TestResult[]
  summary: TestSummary
  runtimeMs: number | null
  memoryKb: number | null
  /**
   * Structured program-level failure: user syntax errors and import-time
   * crashes, with exact editor-relative frames. kind is always
   * 'runtime_error' (the union has only two values); compile-vs-runtime
   * classification is by errorType ('SyntaxError', 'IndentationError', ...).
   * The legacy compilationError/runtimeError strings below are still
   * populated by the backend and act as the fallback when this is absent.
   */
  programFailure?: TestFailure
  compilationError?: string
  runtimeError?: string
  testType?: string
}

/**
 * Result from raw code execution (no test harness)
 */
export interface RawExecutionResult {
  executionId: string
  stdout: string | null
  stderr: string | null
  exitCode: number | null
}

/**
 * Result from async/polling-based submission execution
 */
export interface PollingExecutionResult {
  executionId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  testsTotal?: number | null
  testsPassed?: number | null
  /**
   * These rows come off CodeExecution.testResults through
   * normalizeTestResults, so they ARE TestResults — the field list used to be
   * hand-copied here and silently dropped anything the harness added
   * (inputsAfter, LG-561, was the case that exposed it).
   */
  testResults?: TestResult[] | null
  compileOutput?: string | null
  errorMessage?: string | null
}

/**
 * Unified execution result type that handles run, raw, and polling execution modes
 */
export type ExecutionResult =
  | RunExecutionResult
  | RawExecutionResult
  | PollingExecutionResult

/**
 * Type guard to check if result is from raw execution (no test harness)
 */
export function isRawResult(
  result: ExecutionResult,
): result is RawExecutionResult {
  return (
    'executionId' in result && !('success' in result) && !('status' in result)
  )
}

/**
 * Type guard to check if result is from run mode (synchronous)
 */
export function isRunResult(
  result: ExecutionResult,
): result is RunExecutionResult {
  return 'success' in result && 'results' in result
}

/**
 * Type guard to check if result is from async polling-based submission
 */
export function isPollingResult(
  result: ExecutionResult,
): result is PollingExecutionResult {
  return 'status' in result && !('success' in result) && !('stdout' in result)
}

export type ExecutionMode = 'run' | 'submit' | 'raw'

/**
 * Execution lifecycle as a tagged union — invalid combinations (executing with
 * a settled error, result without a mode) are unrepresentable.
 * `streamedResult` exists because submit streams its result event before the
 * stream closes; the phase stays 'executing' until finishExecution().
 * `token` uniquely identifies this execution (minted by the caller at start).
 * The provider stays mounted across problem navigation, so a slow response
 * from a previous run can resolve after the user has switched and started a
 * new one; the reducer rejects settle actions whose token doesn't match the
 * in-flight one.
 */
export type ExecutionPhase =
  | { status: 'idle' }
  | {
      status: 'executing'
      mode: ExecutionMode
      submitExecutionId: string | null
      streamedResult: ExecutionResult | null
      token: string | null
    }
  | {
      status: 'succeeded'
      mode: ExecutionMode
      result: ExecutionResult
      submitExecutionId: string | null
    }
  | {
      status: 'failed'
      // null when execution never started (e.g. "No problem selected")
      mode: ExecutionMode | null
      error: Error
      result: ExecutionResult | null
      submitExecutionId: string | null
    }

type ExecutionAction =
  | { type: 'start'; mode: ExecutionMode; token: string | null }
  | { type: 'streamStatus'; executionId: string; token: string | null }
  | { type: 'streamResult'; result: ExecutionResult; token: string | null }
  | { type: 'succeed'; result: ExecutionResult; token: string | null }
  | { type: 'fail'; error: Error; token: string | null }
  // A validation error raised before any execution starts (e.g. "No problem
  // selected"). Not tied to an in-flight request, so it carries no token and
  // always records with mode null.
  | { type: 'failValidation'; error: Error }
  | { type: 'finish'; token: string | null }
  | { type: 'clear' }

type ExecutingPhase = Extract<ExecutionPhase, { status: 'executing' }>

/**
 * True only when `phase` is a live execution whose token matches the settling
 * action. This is the race guard: settle/stream actions from a stale request
 * (an earlier run's response resolving after the user switched or re-ran) carry
 * a different token and are ignored.
 */
function isCurrent(
  phase: ExecutionPhase,
  token: string | null,
): phase is ExecutingPhase {
  return phase.status === 'executing' && phase.token === token
}

function executionReducer(
  phase: ExecutionPhase,
  action: ExecutionAction,
): ExecutionPhase {
  switch (action.type) {
    case 'start':
      return {
        status: 'executing',
        mode: action.mode,
        submitExecutionId: null,
        streamedResult: null,
        token: action.token,
      }
    case 'streamStatus':
      // Ignore events from a stale request: `isCurrent` is false when a slower
      // response for a previously-selected problem resolves after the user has
      // switched and started a new run against a different token.
      return isCurrent(phase, action.token)
        ? { ...phase, submitExecutionId: action.executionId }
        : phase
    case 'streamResult':
      // Only applies mid-execution. A `fail` dispatched before `start` settles
      // to `{ mode: null, result: null }`; attaching a late streamResult to a
      // non-executing phase (e.g. that failed state) would produce the
      // unrepresentable-by-design `{ mode: null, result: non-null }`, so this
      // guard is what keeps `failed.result` consistent with `failed.mode`.
      return isCurrent(phase, action.token)
        ? { ...phase, streamedResult: action.result }
        : phase
    case 'succeed':
      return isCurrent(phase, action.token)
        ? {
            status: 'succeeded',
            mode: phase.mode,
            result: action.result,
            submitExecutionId: phase.submitExecutionId,
          }
        : phase
    case 'fail':
      // A settle from an in-flight execution only applies when its token still
      // matches. A stale rejection from a previously-selected problem — whether
      // the phase has since been cleared to idle or a new run is already in
      // flight — is ignored so it can't leak an error onto the current problem.
      return isCurrent(phase, action.token)
        ? {
            status: 'failed',
            mode: phase.mode,
            error: action.error,
            result: phase.streamedResult,
            submitExecutionId: phase.submitExecutionId,
          }
        : phase
    case 'failValidation':
      // Not a settle: a synchronous pre-start validation error for the current
      // problem. Always recorded, with mode null since no execution ran.
      return {
        status: 'failed',
        mode: null,
        error: action.error,
        result: null,
        submitExecutionId: null,
      }
    case 'finish':
      if (!isCurrent(phase, action.token)) return phase
      return phase.streamedResult
        ? {
            status: 'succeeded',
            mode: phase.mode,
            result: phase.streamedResult,
            submitExecutionId: phase.submitExecutionId,
          }
        : { status: 'idle' }
    case 'clear':
      return { status: 'idle' }
    default:
      return assertNever(action)
  }
}

interface ExecutionState {
  phase: ExecutionPhase
  // Derived read fields (backwards-compatible with the pre-union API)
  result: ExecutionResult | null
  error: Error | null
  isExecuting: boolean
  executionMode: ExecutionMode | null
  lastSubmitExecutionId: string | null
  // Transitions. The `token` uniquely identifies each execution; callers mint
  // it at start and pass it back on every settle/stream so the reducer can drop
  // actions from a request that is no longer the one in flight.
  startExecution: (mode: ExecutionMode, token: string | null) => void
  reportStreamStatus: (executionId: string, token: string | null) => void
  reportStreamResult: (result: ExecutionResult, token: string | null) => void
  succeed: (result: ExecutionResult, token: string | null) => void
  fail: (error: Error, token: string | null) => void
  /** Record a pre-start validation error not tied to any execution. */
  failValidation: (error: Error) => void
  finishExecution: (token: string | null) => void
  clear: () => void
}

const ExecutionContext = createContext<ExecutionState | null>(null)

export function ExecutionProvider({ children }: { children: React.ReactNode }) {
  const [phase, dispatch] = useReducer(executionReducer, { status: 'idle' })

  // Execution state belongs to the problem it ran against. The provider stays
  // mounted across problem navigation, so without this reset the previous
  // problem's results leak into the new one — shown in the Test Results pane
  // and injected into the AI assistant's per-message context.
  const problemSlug = useProblemOptional()?.problemSlug ?? null
  const prevSlugRef = useRef(problemSlug)
  useEffect(() => {
    if (prevSlugRef.current === problemSlug) return
    prevSlugRef.current = problemSlug
    dispatch({ type: 'clear' })
  }, [problemSlug])

  const value = useMemo<ExecutionState>(() => {
    const result =
      phase.status === 'executing'
        ? phase.streamedResult
        : phase.status === 'succeeded' || phase.status === 'failed'
          ? phase.result
          : null
    return {
      phase,
      result,
      error: phase.status === 'failed' ? phase.error : null,
      isExecuting: phase.status === 'executing',
      executionMode: phase.status === 'idle' ? null : phase.mode,
      lastSubmitExecutionId:
        phase.status === 'idle' ? null : phase.submitExecutionId,
      startExecution: (mode, token) => dispatch({ type: 'start', mode, token }),
      reportStreamStatus: (executionId, token) =>
        dispatch({ type: 'streamStatus', executionId, token }),
      reportStreamResult: (result, token) =>
        dispatch({ type: 'streamResult', result, token }),
      succeed: (result, token) => dispatch({ type: 'succeed', result, token }),
      fail: (error, token) => dispatch({ type: 'fail', error, token }),
      failValidation: (error) => dispatch({ type: 'failValidation', error }),
      finishExecution: (token) => dispatch({ type: 'finish', token }),
      clear: () => dispatch({ type: 'clear' }),
    }
  }, [phase])

  return <ExecutionContext value={value}>{children}</ExecutionContext>
}

export function useExecution(): ExecutionState {
  const context = use(ExecutionContext)
  if (!context) {
    throw new Error('useExecution must be used within an ExecutionProvider')
  }
  return context
}

export function useExecutionOptional(): ExecutionState | null {
  return use(ExecutionContext)
}
